// Fatia S3b: piloto automático — modo, limites de segurança, regras, simulador, status e execuções.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, Hand, Lock, PauseCircle, PlayCircle, Rocket, ShieldCheck, Sparkles } from 'lucide-react'
import { type AutopilotMode, brl, num } from '@impulso/shared'
import type { AutopilotPolicyDto, UpdateAutopilotPolicyBody } from '@impulso/shared/api/autopilot'
import { useCan, useCompany } from '../../lib/company'
import { errorMessage } from '../../lib/api'
import { ErrorState, Loading, PageHead, Switch } from '../../ui'
import { useToast } from '../../ui/toast'
import { useAutopilotPolicy, useAutopilotStatus, usePauseResume, useRunNow, useUpdatePolicy } from '../../features/autopilot/api'
import RulesCard from '../../features/autopilot/RulesCard'
import SimulatorCard from '../../features/autopilot/SimulatorCard'
import RunsTimeline from '../../features/autopilot/RunsTimeline'

const MODES: Array<{ id: AutopilotMode; icon: typeof Eye; title: string; desc: string; rec?: boolean }> = [
  { id: 'suggest', icon: Eye, title: 'Só sugerir', desc: 'O Seu Impulso analisa e sugere. Nada muda sem o seu "sim".' },
  { id: 'copilot', icon: Hand, title: 'Copiloto', desc: 'Faz ajustes pequenos sozinho (dentro da variação máxima) e pede aprovação para o resto.', rec: true },
  { id: 'auto', icon: Rocket, title: 'Automático', desc: 'Cuida de tudo dentro dos limites abaixo e te manda um resumo por dia.' },
]

interface LimitsForm { monthlyCap: string; target: string; maxPct: number; requireApproval: boolean; quiet: boolean; quietStart: string; quietEnd: string }

function toForm(p: AutopilotPolicyDto): LimitsForm {
  return {
    monthlyCap: p.monthlyCap == null ? '' : String(p.monthlyCap),
    target: p.targetCostPerResult == null ? '' : String(Math.round(p.targetCostPerResult * 100) / 100),
    maxPct: p.maxDailyChangePct,
    requireApproval: p.requireApprovalTotalIncrease,
    quiet: !!p.quietHours,
    quietStart: p.quietHours?.start ?? '22:00',
    quietEnd: p.quietHours?.end ?? '06:00',
  }
}

export default function Autopilot() {
  const company = useCompany()
  const canEdit = useCan('company.configure_autopilot')
  const toast = useToast()
  const policy = useAutopilotPolicy(company.id)
  const status = useAutopilotStatus(company.id)
  const update = useUpdatePolicy(company.id)
  const pauseResume = usePauseResume(company.id)
  const runNow = useRunNow(company.id)
  const [form, setForm] = useState<LimitsForm | null>(null)

  useEffect(() => { if (policy.data && !form) setForm(toForm(policy.data)) }, [policy.data, form])

  if (policy.isLoading) return <><PageHead title="Piloto automático" /><Loading lines={6} /></>
  if (policy.isError || !policy.data) return <><PageHead title="Piloto automático" /><ErrorState error={policy.error} onRetry={() => void policy.refetch()} /></>
  const p = policy.data
  const st = status.data

  const save = (body: UpdateAutopilotPolicyBody, msg = 'Configuração salva.') =>
    update.mutate(body, { onSuccess: () => toast(msg), onError: (err) => toast(errorMessage(err)) })

  const saveLimits = () => {
    if (!form) return
    const cap = form.monthlyCap.trim() === '' ? null : Number(form.monthlyCap.replace(',', '.'))
    const target = form.target.trim() === '' ? null : Number(form.target.replace(',', '.'))
    if ((cap != null && !(cap >= 0)) || (target != null && !(target >= 0))) { toast('Confira os valores: use só números.'); return }
    save({ monthlyCap: cap, targetCostPerResult: target, maxDailyChangePct: form.maxPct, requireApprovalTotalIncrease: form.requireApproval, quietHours: form.quiet ? { start: form.quietStart, end: form.quietEnd } : null }, 'Limites salvos.')
  }

  const ticket = company.avgTicket ?? null
  const conv = company.conversionRate ?? null
  const leaseOther = st?.leases.find((l) => !l.heldByThisCompany)

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Piloto automático" sub="Você decide quanto o Seu Impulso pode fazer sozinho. Os limites de dinheiro valem sempre, em qualquer modo.">
        {canEdit && (
          <button className="btn sm" disabled={runNow.isPending} onClick={() => runNow.mutate(undefined, {
            onSuccess: (r) => toast(r.proposals ? `Análise feita: ${r.executed} mudanças, ${r.recommended} sugestões.` : (r.notes[0] ?? 'Análise feita: nada a fazer agora.')),
            onError: (err) => toast(errorMessage(err)),
          })}>
            <Sparkles size={15} /> {runNow.isPending ? 'Analisando…' : 'Analisar agora'}
          </button>
        )}
      </PageHead>

      {(p.paused || st?.killSwitch || leaseOther) && (
        <div className="stack" style={{ gap: 8 }}>
          {p.paused && (
            <div className="callout amber">
              <PauseCircle size={18} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="strong">O piloto está pausado</div>
                <div className="small">{p.pausedReason ?? 'Nenhuma mudança automática será feita.'} Enquanto isso, ele só sugere.</div>
              </div>
              {canEdit && <button className="btn sm" disabled={pauseResume.isPending} onClick={() => pauseResume.mutate({ pause: false }, { onSuccess: () => toast('Piloto retomado.'), onError: (e) => toast(errorMessage(e)) })}><PlayCircle size={15} /> Retomar</button>}
            </div>
          )}
          {st?.killSwitch && <div className="callout amber"><ShieldCheck size={18} /><span className="small">As mudanças automáticas estão temporariamente desligadas pela equipe do Seu Impulso. As sugestões continuam normalmente.</span></div>}
          {leaseOther && <div className="callout amber"><Lock size={18} /><span className="small">Esta conta de anúncios também está ligada em outra conta do Seu Impulso, que já controla as mudanças automáticas. Aqui o piloto só sugere, para não haver dois robôs mexendo no mesmo orçamento.</span></div>}
        </div>
      )}

      <div className="grid g-3">
        {MODES.map((m) => {
          const allowed = p.allowedModes.includes(m.id)
          const on = p.mode === m.id
          return (
            <button key={m.id} type="button" className={`option-card ${on ? 'on' : ''}`} aria-pressed={on} disabled={!canEdit || update.isPending || (!allowed && !on)}
              onClick={() => !on && allowed && save({ mode: m.id }, `Modo "${m.title}" ativado.`)}
              style={{ flexDirection: 'column', opacity: allowed ? 1 : 0.7, textAlign: 'left' }}>
              <div className="row" style={{ width: '100%' }}>
                <span className="ic purple"><m.icon size={18} /></span>
                {m.rec && allowed && <span className="badge purple">Recomendado</span>}
                {!allowed && <span className="badge"><Lock size={12} /> Plano superior</span>}
                <span className="radio" />
              </div>
              <div>
                <div className="strong" style={{ fontSize: 15 }}>{m.title}</div>
                <div className="small muted mt-8">{m.desc}</div>
              </div>
            </button>
          )
        })}
      </div>
      {p.allowedModes.length < 3 && (
        <p className="small muted">Seu plano inclui só o modo "Só sugerir". <Link className="strong" to={`/app/conta/${company.accountId}/plano`}>Conheça os planos</Link> para liberar o copiloto e o automático.</p>
      )}

      {/* três áreas: no computador os limites ficam à esquerda; no celular a situação do piloto (e o "Pausar") vem primeiro */}
      <div className="ap-main">
        <div className="stack ap-limits">
          {form && (
            <div className="card">
              <div className="row" style={{ gap: 10, marginBottom: 6 }}>
                <span className="ic green"><ShieldCheck size={18} /></span>
                <div>
                  <h2>Seus limites de segurança</h2>
                  <div className="small muted">O Seu Impulso nunca ultrapassa esses valores, nem no modo automático.</div>
                </div>
              </div>
              <div className="stack mt-16" style={{ gap: 18 }}>
                <div className="field">
                  <label htmlFor="cap">Gasto máximo por mês (Google + Meta), em R$</label>
                  <input id="cap" className="input" inputMode="decimal" disabled={!canEdit} value={form.monthlyCap} onChange={(e) => setForm({ ...form, monthlyCap: e.target.value })} placeholder="Ex.: 1500" />
                  <span className="small muted">Conta o que já foi gasto no mês mais os orçamentos diários até o fim do mês.</span>
                </div>
                <div className="field">
                  <label htmlFor="target">Custo máximo aceitável por cliente, em R$</label>
                  <input id="target" className="input" inputMode="decimal" disabled={!canEdit} value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="Ex.: 15" />
                  {ticket && conv ? <span className="small muted">Com ticket médio de {brl(ticket)} e {num(conv * 100)}% de conversão, acima de {brl(ticket * conv * 0.5)} você começa a ganhar pouco por venda.</span> : <span className="small muted">Se ficar em branco, usamos a sua média histórica.</span>}
                </div>
                <div className="field">
                  <label htmlFor="maxpct">Variação máxima por dia em cada campanha: <b>{form.maxPct}%</b></label>
                  <input id="maxpct" type="range" className="range" min={5} max={30} step={1} disabled={!canEdit} value={form.maxPct} onChange={(e) => setForm({ ...form, maxPct: Number(e.target.value) })} />
                </div>
                <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
                  <Switch on={form.requireApproval} disabled={!canEdit} label="Pedir aprovação para aumentar o orçamento total" onChange={(v) => setForm({ ...form, requireApproval: v })} />
                  <div>
                    <div className="strong">Sempre pedir minha aprovação antes de aumentar o orçamento total</div>
                    <div className="small muted">Mover verba entre campanhas continua automático.</div>
                  </div>
                </div>
                <div className="row wrap" style={{ alignItems: 'flex-start', gap: 12 }}>
                  <Switch on={form.quiet} disabled={!canEdit} label="Janela de silêncio" onChange={(v) => setForm({ ...form, quiet: v })} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div className="strong">Janela de silêncio</div>
                    <div className="small muted">Nenhuma mudança automática nesse horário.</div>
                    {form.quiet && (
                      <div className="row wrap mt-8" style={{ gap: 8 }}>
                        <input className="input" type="time" aria-label="Início" style={{ width: 'auto' }} value={form.quietStart} disabled={!canEdit} onChange={(e) => setForm({ ...form, quietStart: e.target.value })} />
                        <span className="small muted">até</span>
                        <input className="input" type="time" aria-label="Fim" style={{ width: 'auto' }} value={form.quietEnd} disabled={!canEdit} onChange={(e) => setForm({ ...form, quietEnd: e.target.value })} />
                      </div>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="row wrap" style={{ gap: 8 }}>
                    <button className="btn primary sm" disabled={update.isPending} onClick={saveLimits}>{update.isPending ? 'Salvando…' : 'Salvar limites'}</button>
                    <button className="btn ghost sm" onClick={() => setForm(toForm(p))}>Desfazer alterações</button>
                  </div>
                )}
              </div>
            </div>
          )}

          <RulesCard policy={p} canEdit={canEdit} saving={update.isPending} onChange={(rules) => save({ rules })} />
        </div>

        <div className="card ap-status">
          <div className="row" style={{ gap: 10 }}>
            <span className="ic purple"><Sparkles size={18} /></span>
            <div style={{ flex: 1 }}>
              <h3>Situação do piloto</h3>
              <div className="small muted">
                {p.paused ? 'Pausado' : st?.effectiveMode === 'suggest' ? 'Só sugerindo' : st?.effectiveMode === 'copilot' ? 'Copiloto ativo' : 'Automático ativo'}
              </div>
            </div>
            {canEdit && !p.paused && (
              <button className="btn ghost sm" disabled={pauseResume.isPending} onClick={() => pauseResume.mutate({ pause: true }, { onSuccess: () => toast('Piloto pausado.'), onError: (e) => toast(errorMessage(e)) })}>
                <PauseCircle size={15} /> Pausar
              </button>
            )}
          </div>
          {st && st.impact.measured > 0 && (() => {
            const i = st.impact
            const tone = i.extraResults < 0 ? 'amber' : i.extraResults > 0 || i.savings > 0 ? 'green' : ''
            return (
              <div className={`callout ${tone} mt-16`}>
                <span className="small">
                  Resultado das ações automáticas: <b>{i.extraResults > 0 ? '+' : ''}{num(i.extraResults)} clientes</b> a mais ou a menos do esperado
                  {i.savings > 0 && <>, <b>{brl(i.savings)} economizados</b> sem perder clientes</>}
                  {i.extraSpend > 0 && <>, <b>{brl(i.extraSpend)} gastos a mais</b></>} ({i.measured} mudanças medidas).
                </span>
              </div>
            )
          })()}
          {st && st.impact.actions > 0 && st.impact.measured === 0 && <p className="small muted mt-12">O piloto fez {st.impact.actions} mudança(s). Medimos o resultado 7 dias depois de cada uma.</p>}
          <h3 className="mt-16">O que o piloto fez</h3>
          <RunsTimeline companyId={company.id} />
          <Link className="btn ghost sm mt-12" to={`/app/${company.id}/historico`}>Ver histórico completo</Link>
        </div>
        <div className="ap-sim"><SimulatorCard companyId={company.id} defaultMode={p.mode} /></div>
      </div>
    </div>
  )
}
